// dev-product-carousel — Marqo-driven product carousel that reuses the Dev
// Collection PLP card.
//
// Mounted by sections/dev-product-carousel.liquid as <dev-product-carousel>.
// It fetches hits from window.Marqo (collections / recommendations — published
// by assets/marqo.js, gated on settings.marqo_enabled) and renders them with
// Marqo.buildCard(hits, cardEl), cloning the same <template data-marqo-card> the
// PLP uses. buildCard wires each card internally: the swatch thumbnail strip's
// prev/next <button>s SCROLL the strip (they never navigate), prices use
// <money-set>, reviews/labels are populated. This file only fetches the hits and
// drives the OUTER prev/next card scrolling.
//
// money-set is the same tiny element dev-collection.js ships; it is registered
// here too because that file only loads on the collection page.

(function () {
  'use strict';

  const ELEMENT_NAME = 'dev-product-carousel';
  if (customElements.get(ELEMENT_NAME)) return;

  // attributesToRetrieve whitelist — mirrors app/scripts/common/Marqo/
  // productCard/attributes.js PLP_ATTRIBUTES_TO_RETRIEVE (also duplicated in
  // dev-collection.js/dev-navbar.js/dev-ai-agent.js — this file isn't part of
  // the esbuild bundle so it can't import the shared constant). RD's Marqo
  // docs can be ~1MB each; without this every recommendation/collection/
  // search call pulls full documents.
  const PLP_ATTRIBUTES = [
    '_id', 'parentProductId', 'productTags', 'productTitle', 'productTitle_fr',
    'productVendor', 'productHandle', 'productHandle_fr', 'variantImageUrl', 'productImageUrls',
    'siblingImageUrls', 'siblingIds', 'siblingVariantIds', 'price', 'priceMin',
    'priceMax', 'comparePrice', 'promoPrice', 'promoPriceMin', 'promoPriceMax', 'maxDiscount', 'maxPromoDiscount',
    'mfield_custom_call_for_price', 'productOutOfStock',
    'productMetafieldCustomCurrentInventoryNew', 'productMetafieldCustomFitmentScope',
    'productMetafieldFitmentFitmentScope', 'productMetafieldCustomPlpCardTag',
  ];

  // Same precedence as app/scripts/common/Marqo/util/getMarqoUserId.js
  // (bundled version used by <marqo-recommend>) — duplicated here because
  // this file isn't part of that esbuild bundle. Reads the Marqo pixel's
  // anonymous visitor id so "For You" queries the same id the pixel wrote
  // its events under; without this the read/write paths diverge and
  // personalization never fires for anonymous shoppers.
  function getMarqoUserId() {
    try {
      const match = document.cookie.match(/(?:^|;\s*)marqo_user_id=([^;]+)/);
      if (match && match[1]) {
        try { return decodeURIComponent(match[1]); } catch (_) { return match[1]; }
      }
    } catch (_) { /* cookie access blocked */ }
    try {
      const v = localStorage.getItem('marqo_user_id');
      if (v) return v;
    } catch (_) { /* storage blocked */ }
    try {
      const api = window.MARQO_PIXEL_WEBSITE;
      if (api && typeof api.getUserId === 'function') {
        const v = api.getUserId();
        if (v) return v;
      }
    } catch (_) { /* ignore */ }
    return '';
  }

  // ── money formatting (mirrors dev-collection.js) ──────────────────────
  let MONEY_FORMAT = '${{amount}}';
  let MONEY_CURRENCY = 'USD';

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimal = decimal == null ? '.' : decimal;
    const value = (Number(number) || 0).toFixed(precision);
    const parts = value.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const cents = parts[1] ? decimal + parts[1] : '';
    return dollars + cents;
  }

  function formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    const fmt = MONEY_FORMAT;
    if (!fmt || !/\{\{\s*\w+\s*\}\}/.test(fmt)) {
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: MONEY_CURRENCY || 'USD' }).format(amount);
      } catch (_) {
        return '$' + formatWithDelimiters(amount, 2);
      }
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_m, name) {
      switch (name) {
        case 'amount': return formatWithDelimiters(amount, 2, ',', '.');
        case 'amount_no_decimals': return formatWithDelimiters(amount, 0, ',', '.');
        case 'amount_with_comma_separator': return formatWithDelimiters(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return formatWithDelimiters(amount, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return formatWithDelimiters(amount, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return formatWithDelimiters(amount, 0, ' ', '.');
        case 'amount_with_space_separator': return formatWithDelimiters(amount, 2, ' ', ',');
        default: return formatWithDelimiters(amount, 2, ',', '.');
      }
    });
  }

  if (!customElements.get('money-set')) {
    customElements.define('money-set', class extends HTMLElement {
      set(cents) {
        const n = Number(cents);
        if (!Number.isFinite(n)) return;
        if (n <= 0) { this.textContent = ''; this.closest('.dev-plp-card__price')?.classList.add('dev-plp-card__price--unpriced'); return; }
        this.textContent = formatMoney(n);
      }
    });
  }

  function marqo() { return window.Marqo; }

  // marqo.js is a low-priority module; it may upgrade after this one. Poll briefly.
  function whenMarqoReady(timeoutMs) {
    if (marqo()) return Promise.resolve(marqo());
    return new Promise((resolve) => {
      const start = Date.now();
      const id = setInterval(() => {
        if (marqo() || Date.now() - start > timeoutMs) {
          clearInterval(id);
          resolve(marqo() || null);
        }
      }, 60);
    });
  }

  // GA4 dataLayer tracker — ported from RDI's app/scripts/common/Marqo/
  // recommendations/gtmTracker.js (event contract: render_recommendation,
  // impression_recommendation, click_impression, click_recommendation).
  // Duplicated here rather than imported since this file isn't part of the
  // esbuild bundle (same rationale as PLP_ATTRIBUTES/getMarqoUserId above).
  // Only setupClickImpressionTracking's selectors differ from RDI's, since
  // this carousel drives its own prev/next buttons instead of Swiper.
  function createGtmTracker() {
    // `<marqo_index_name>-<locale>` (e.g. "shopify-revco123-en"), both
    // already exposed globally by snippets/scripts.liquid. Unlike RDI, Revco
    // never ran SearchSpring and has no legacy site_id setting/window.theme.siteId
    // to fall back to — so if indexName/locale are ever both missing, this
    // returns null rather than inventing one.
    function resolveSiteId() {
      const indexName = window.theme && window.theme.marqo && window.theme.marqo.indexName;
      const locale = window.theme && window.theme.locale;
      if (indexName && locale) return `${indexName}-${locale}`;
      return (window.theme && window.theme.siteId) || null;
    }

    function pushRenderEvent(tag, placement, seed) {
      seed = seed || {};
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'render_recommendation',
        tag: tag,
        placement: placement,
        seed_id: seed.id || null,
        seed_sku: seed.sku || null,
        site_id: resolveSiteId(),
      });
    }

    // Only fires when recommended products come into viewport.
    function pushImpressionEvent(tag, placement, seed) {
      seed = seed || {};
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'impression_recommendation',
        tag: tag,
        placement: placement,
        seed_id: seed.id || null,
        seed_sku: seed.sku || null,
        site_id: resolveSiteId(),
        impression_timestamp: Date.now(),
        viewport_triggered: true,
      });
    }

    // Fires on carousel navigation (prev/next arrows) — no specific product
    // is targeted by these interactions.
    function pushClickImpressionEvent(tag, placement, seed) {
      seed = seed || {};
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'click_impression',
        tag: tag,
        placement: placement,
        seed_id: seed.id || null,
        seed_sku: seed.sku || null,
        site_id: resolveSiteId(),
        click_id: null,
        click_sku: null,
      });
    }

    // Fires when a recommended product card is clicked.
    function pushClickRecommendationEvent(tag, placement, seed, clickedProductId) {
      seed = seed || {};
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'click_recommendation',
        tag: tag,
        placement: placement,
        seed_id: seed.id || null,
        seed_sku: seed.sku || null,
        click_id: clickedProductId || null,
        click_sku: null,
        site_id: resolveSiteId(),
      });
    }

    // Fires impression_recommendation once, the first time any card in
    // `scope` crosses 50% visibility.
    function setupIntersectionObserver(scope, tag, placement, seed) {
      const cards = scope.querySelectorAll('.marqo-product-card');
      if (!cards.length) return null;

      let hasFired = false;
      const observer = new IntersectionObserver((entries) => {
        if (hasFired) return;
        const anyVisible = entries.some((entry) => entry.isIntersecting);
        if (!anyVisible) return;

        hasFired = true;
        pushImpressionEvent(tag, placement, seed);
        observer.disconnect();
      }, {
        threshold: 0.5,
        rootMargin: '0px 0px -50px 0px',
      });

      cards.forEach((card) => observer.observe(card));
      return observer;
    }

    // Wires click_impression to this carousel's own prev/next buttons and
    // touch/swipe gestures within `scope` (the <dev-product-carousel> host —
    // this carousel has no Swiper nav/pagination/scrollbar to hook into).
    function setupClickImpressionTracking(scope, tag, placement, seed) {
      if (!scope) return;

      scope.querySelectorAll('[data-pcar-prev], [data-pcar-next]').forEach((el) => {
        el.addEventListener('click', () => pushClickImpressionEvent(tag, placement, seed));
      });

      let touchTimeout;
      scope.addEventListener('touchend', () => {
        clearTimeout(touchTimeout);
        touchTimeout = setTimeout(() => pushClickImpressionEvent(tag, placement, seed), 300);
      });
    }

    return {
      pushRenderEvent,
      pushImpressionEvent,
      pushClickImpressionEvent,
      pushClickRecommendationEvent,
      setupIntersectionObserver,
      setupClickImpressionTracking,
    };
  }

  customElements.define(
    ELEMENT_NAME,
    class extends HTMLElement {
      #initialized = false;
      #cardTemplate = null;
      #tracker = createGtmTracker();

      async connectedCallback() {
        if (this.#initialized) return;
        this.#initialized = true;

        this.track = this.querySelector('[data-pcar-track]');
        this.prevBtn = this.querySelector('[data-pcar-prev]');
        this.nextBtn = this.querySelector('[data-pcar-next]');
        if (!this.track) return;

        if (this.dataset.moneyFormat) MONEY_FORMAT = this.dataset.moneyFormat;
        if (this.dataset.currency) MONEY_CURRENCY = this.dataset.currency;

        // The card ELEMENT lives inside the <template> — buildCard clones it.
        this.#cardTemplate = this.querySelector('template[data-marqo-card]')
          ?.content?.querySelector('.marqo-product-card') || null;
        if (!this.#cardTemplate) return this.#fail();

        const M = await whenMarqoReady(8000);
        if (!M) {
          console.error(`[${ELEMENT_NAME}] window.Marqo missing — is settings.marqo_enabled on?`);
          return this.#fail();
        }

        let hits = [];
        try {
          hits = await this.#fetchHits(M);
        } catch (err) {
          console.error(`[${ELEMENT_NAME}] fetch failed`, err);
        }
        if (!hits.length) return this.#fail();

        // Mirror the PLP's post-buildCard steps (dev-collection.js #render). Marqo's
        // productCard defers secondary images to data-src/data-bg and caps the swatch
        // strip at 5 — and this theme ships no lazy-loader or swatch-change handler.
        // Without these the swatch thumbnails render broken and the arrows misbehave.
        const frag = M.buildCard(hits, this.#cardTemplate);
        this.#appendExtraSwatches(frag, hits); // add swatches Marqo capped past 5
        this.#hydrateLazyImages(frag);         // data-src → src, data-bg → backgroundImage
        this.track.appendChild(frag);
        this.#fitSwatches(this.track);         // drop swatch arrows where thumbnails already fit
        this.#bindNav();
        this.#bindCardInteractions();
        this.#updateButtons();
        this.#trackRender();
      }

      disconnectedCallback() {
        this.prevBtn?.removeEventListener('click', this.onPrev);
        this.nextBtn?.removeEventListener('click', this.onNext);
        this.track?.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        this.removeEventListener('change', this.onChange);
        window.removeEventListener('resize', this.onSwatchResize);
      }

      // ── data ──────────────────────────────────────────────────────────
      // Mirrors app/scripts/common/Marqo/recommendations/element.js
      // (<marqo-recommend>'s #fetch/#fetchFallback) — same kind dispatch,
      // same seed source (the CURRENT product, not recently-viewed history),
      // same stock/attributesToRetrieve trimming on every request.
      async #fetchHits(M) {
        const limit = Number(this.dataset.limit) || 12;
        const kind = this.dataset.kind || 'collection';
        const baseFilter = this.dataset.filter || undefined;
        const common = { limit, filter: baseFilter, attributesToRetrieve: PLP_ATTRIBUTES };

        // Computed once, reused by both the primary recommendation attempt
        // and the fallback below — see #filterWithFitment. undefined for
        // 'collection'/'recent-view'/'for-you' (not vehicle-specific concepts).
        const fitmentFilter = kind !== 'collection' ? this.#filterWithFitment(kind, baseFilter, M) : undefined;

        if (kind !== 'collection') {
          const recCommon = { ...common, filter: fitmentFilter };
          const hits = await this.#fetchRecommendations(M, kind, recCommon);
          if (hits.length) return hits;
        }
        return this.#fetchFallback(M, common, fitmentFilter);
      }

      // Scope "similar"/"complementary" to the vehicle the shopper landed
      // with — e.g. clicked through from a Shop My Ride-filtered collection,
      // or arrived via a same-origin referrer carrying filter.fitments.
      // Mirrors dev-shop-my-ride.js's #incomingFitment()/#applyIncomingRide()
      // deep-link contract (duplicated below, not imported — independent
      // lazy-loaded module scripts with no ordering guarantee between them;
      // same rationale RDI documents for its own resolvePreselectedVehicle.js).
      // Doesn't re-validate the vehicle against THIS product's own fitment
      // table (that's dev-shop-my-ride's job, for its own ride cascade) — it
      // just AND-combines a fitmentKeys clause so recommended products are
      // scoped to the same vehicle; an incompatible/stale vehicle just yields
      // fewer/zero hits and falls through to #fetchFallback like any other
      // empty recommendation result.
      #filterWithFitment(kind, baseFilter, M) {
        if (kind !== 'similar' && kind !== 'complementary') return baseFilter;
        const vehicle = this.#incomingFitment();
        if (!vehicle) return baseFilter;
        const fitmentClause = M.buildFilter([[M.URL_PARAMS.fitment, vehicle]]);
        if (!fitmentClause) return baseFilter;
        return baseFilter ? `${baseFilter} AND ${fitmentClause}` : fitmentClause;
      }

      // Exact same contract as dev-shop-my-ride.js#incomingFitment(): prefer
      // this page's own URL, then fall back to a same-origin referrer's query.
      #incomingFitment() {
        const KEY = 'filter.fitments';
        const read = (search) => {
          try { return new URLSearchParams(search).get(KEY); } catch (e) { return null; }
        };
        const here = read(location.search);
        if (here) return here;
        const ref = document.referrer;
        if (!ref) return null;
        try {
          const u = new URL(ref);
          if (u.origin !== location.origin) return null; // only trust a same-origin referrer
          return read(u.search);
        } catch (e) { return null; }
      }

      async #fetchRecommendations(M, kind, common) {
        const recs = M.recommendations;
        if (!recs) return [];

        if (kind === 'recent-view') {
          const excludeIds = this.#parentIds();
          const res = await recs.fetchRecentlyViewed({
            limit: common.limit,
            excludeIds,
            attributesToRetrieve: common.attributesToRetrieve,
          });
          return (res && res.hits) || [];
        }

        if (kind === 'for-you') {
          // Prefer the Marqo pixel's own anonymous id — it's what wrote the
          // click/cart/purchase events this endpoint ranks against. Falls
          // back to data-user-id (Liquid-set customer.id) when the pixel
          // hasn't written one yet.
          const userId = getMarqoUserId() || this.dataset.userId;
          if (!userId) return [];
          const res = await recs.forYou({ ...common, userId });
          return (res && res.hits) || [];
        }

        // similar / complementary are seeded by the product(s) THIS carousel
        // is rendered on, resolved to real Marqo _ids — not by the shopper's
        // recently-viewed history, which would seed "You May Also Like" with
        // the wrong product on a fresh session (no history) or an unrelated
        // one. On the cart page, data-cart-seeds carries every LINE ITEM
        // (productId + sku) so two lines sharing a product — e.g. the same
        // sunglasses in Amber and Smoke — each resolve to their OWN variant
        // instead of collapsing to one; elsewhere (PDP) it's just
        // data-parent-product-ids, a single product. Colour-sibling
        // expansion (data-expand-siblings) is right for that single-product
        // case but wrong for the cart's — see dev-product-carousel.liquid's
        // "Expand seeds to colour siblings" setting.
        if (!recs.resolveSeedIds) return [];
        const seedInput = this.#cartSeeds();
        const parents = seedInput.length ? seedInput : this.#parentIds();
        if (!parents.length) return [];
        const expandSiblings = this.dataset.expandSiblings !== 'false';
        const seeds = await recs.resolveSeedIds(parents, { expandSiblings }).catch(() => []);
        if (!seeds.length) return [];

        if (kind === 'complementary') {
          const res = await recs.complementary({
            ...common,
            docIds: seeds,
            eventTypes: this.#eventTypes(),
          });
          return (res && res.hits) || [];
        }
        const res = await recs.similar({ ...common, documentIds: seeds });
        return (res && res.hits) || [];
      }

      // Generic fallback so the carousel never renders empty: the merchant's
      // configured collection, else a wildcard search — same two-step as
      // <marqo-recommend>'s #fetchFallback.
      //
      // Tries the SAME vehicle scope as the primary recommendation attempt
      // first (so a shopper mid-ride still sees fitment-relevant fallback
      // results, not just "show something"), then drops to the unscoped
      // base filter as the guaranteed-non-empty backstop. Skips the scoped
      // attempt entirely when fitmentFilter is the same as common.filter
      // (nothing to gain — e.g. no incoming vehicle was detected).
      async #fetchFallback(M, common, fitmentFilter) {
        if (fitmentFilter && fitmentFilter !== common.filter) {
          const hits = await this.#fetchFallbackOnce(M, { ...common, filter: fitmentFilter });
          if (hits.length) return hits;
        }
        return this.#fetchFallbackOnce(M, common);
      }

      async #fetchFallbackOnce(M, common) {
        const name = this.dataset.collectionName || '';
        if (name) {
          const res = await M.collections({ collectionName: name, ...common });
          if (res && res.hits && res.hits.length) return res.hits;
        }
        const res = await M.search({ q: '*', limit: common.limit, filter: common.filter, attributesToRetrieve: common.attributesToRetrieve });
        return (res && res.hits) || [];
      }

      #parentIds() {
        return (this.dataset.parentProductIds || '')
          .split(',').map((s) => s.trim()).filter(Boolean);
      }

      // data-cart-seeds is a JSON array of {productId, sku} — one entry per
      // cart LINE ITEM (not deduped by product), so recs.resolveSeedIds can
      // match each to its own exact variant doc by SKU. Empty/absent outside
      // the cart page.
      #cartSeeds() {
        const raw = this.dataset.cartSeeds;
        if (!raw) return [];
        try {
          const parsed = JSON.parse(raw);
          if (!Array.isArray(parsed)) return [];
          return parsed
            .map((it) => ({
              parentProductId: String((it && it.productId) || '').trim(),
              sku: it && it.sku ? String(it.sku).trim() : '',
            }))
            .filter((it) => it.parentProductId);
        } catch (_) {
          return [];
        }
      }

      // Comma-split data-event-type (e.g. "AddToCartEvent,PurchaseEvent" for
      // "Customers Also Bought") into the array complementary() expects.
      // Empty → complementary.js defaults to ['ClickEvent'].
      #eventTypes() {
        return (this.dataset.eventType || '')
          .split(',').map((s) => s.trim()).filter(Boolean);
      }

      #fail() {
        // hide-when-empty: a carousel should never leave a blank gap
        this.style.display = 'none';
      }

      // ── GA4 dataLayer tracking ───────────────────────────────────────
      // Ports RDI's <marqo-recommend> tracking contract (render/impression/
      // click_impression/click_recommendation) — see createGtmTracker above.
      #trackRender() {
        const tag = this.dataset.tag || this.dataset.kind || '';
        const placement = this.dataset.placement || '';
        // seed_sku only set on product pages (see data-seed-sku in
        // sections/dev-product-carousel.liquid) — the seeding product's
        // active variant SKU.
        const seed = { id: this.#parentIds()[0] || null, sku: this.dataset.seedSku || null };

        this.#tracker.pushRenderEvent(tag, placement, seed);
        this.#tracker.setupIntersectionObserver(this.track, tag, placement, seed);
        this.#tracker.setupClickImpressionTracking(this, tag, placement, seed);

        this.track.addEventListener('click', (event) => {
          const card = event.target.closest && event.target.closest('.marqo-product-card');
          if (!card) return;
          this.#tracker.pushClickRecommendationEvent(tag, placement, seed, card.dataset.productId);
        });
      }

      // ── outer card scrolling ──────────────────────────────────────────
      #bindNav() {
        if (!this.prevBtn || !this.nextBtn) return;
        this.onPrev = () => this.#scrollByPage(-1);
        this.onNext = () => this.#scrollByPage(1);
        this.onScroll = () => this.#updateButtons();
        this.prevBtn.addEventListener('click', this.onPrev);
        this.nextBtn.addEventListener('click', this.onNext);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
      }

      // ── reused PLP card behaviour (ported from dev-collection.js) ────────
      // Marqo's productCard wires the swatch radios but ships no change handler,
      // and no variant JS runs here — so swap the main image ourselves on click.
      // Also re-fit the swatch arrows on resize, since card width is responsive.
      #bindCardInteractions() {
        this.onChange = (e) => {
          const swt = e.target.closest && e.target.closest('.dev-plp-card__swt-input');
          if (swt) this.#onSwatchChange(swt);
        };
        this.onSwatchResize = () => this.#fitSwatches(this.track);
        this.addEventListener('change', this.onChange);
        window.addEventListener('resize', this.onSwatchResize, { passive: true });
      }

      // Marqo's productCard defers swatch (and other secondary) images to
      // data-src / data-bg, expecting a lazy-loader to swap them in. This theme
      // ships none, so those imgs would keep src="" and render broken. Swap them
      // ourselves on the freshly-built fragment. (The main card image is set via
      // .src directly upstream, so it's unaffected.)
      #hydrateLazyImages(root) {
        root.querySelectorAll('img[data-src]').forEach((img) => {
          if (img.dataset.src) { img.src = img.dataset.src; img.removeAttribute('data-src'); }
        });
        root.querySelectorAll('[data-bg]').forEach((el) => {
          if (el.dataset.bg) el.style.backgroundImage = `url("${el.dataset.bg}")`;
        });
      }

      // Hide the swatch prev/next arrows on any card whose thumbnails already fit
      // the row. Compare the track's natural content width against the full wrapper
      // width so the decision is "do the thumbnails fit?" independent of the arrows.
      #fitSwatches(root) {
        (root || this).querySelectorAll('.dev-plp-card__swatches').forEach((wrap) => {
          const track = wrap.querySelector('.dev-plp-card__swt-track');
          if (!track) { wrap.classList.add('dev-plp-card__swatches--no-nav'); return; } // ≤1 swatch
          if (!wrap.clientWidth) return; // wrapper hidden right now — re-checked on resize
          const fits = track.scrollWidth <= wrap.clientWidth + 1; // +1 for sub-pixel rounding
          wrap.classList.toggle('dev-plp-card__swatches--no-nav', fits);
        });
      }

      // Marqo caps the swatch carousel at 5 colours (Nt=5 in marqo.js). Mirror its
      // item builder (Ot): dedupe by colour exactly like Marqo (qt), then append the
      // swatches it didn't render. cards ↔ hits are 1:1 in buildCard's output order.
      #appendExtraSwatches(root, hits) {
        const cards = root.querySelectorAll('.marqo-product-card');
        cards.forEach((card, i) => {
          const hit = hits[i];
          if (!hit) return;
          const all = this.#dedupeSwatches(hit.swatches);
          const track = card.querySelector('.dev-plp-card__swt-track');
          const tpl = track && track.querySelector('template');
          if (!track || !tpl) return; // single-variant card: Marqo removed the slider
          const rendered = track.querySelectorAll('.dev-plp-card__swt-item').length;
          if (all.length <= rendered) return; // nothing was capped
          const proto = tpl.content.firstElementChild;
          const name = track.querySelector('.dev-plp-card__swt-input')?.name || 'productSwatch';
          all.slice(rendered).forEach((sw, k) => {
            const item = proto.cloneNode(true);
            const id = `${name}-x${rendered + k}`;
            const url = this.#resizeImg(sw.img, 150); // match Ot()'s 150×150 swatch
            const input = item.querySelector('input');
            const label = item.querySelector('label');
            const img = item.querySelector('img');
            const holder = item.querySelector('.image-holder');
            if (input) { input.id = id; input.name = name; }
            if (label) label.setAttribute('for', id);
            if (img) { img.setAttribute('data-src', url); img.alt = sw.colour || ''; img.removeAttribute('srcset'); }
            if (holder) { holder.setAttribute('data-bg', url); holder.setAttribute('data-swt', url); }
            track.appendChild(item);
          });
        });
      }

      // Dedupe swatches by colour, preserving order — mirrors marqo.js qt().
      #dedupeSwatches(swatches) {
        if (!Array.isArray(swatches)) return [];
        const seen = new Set();
        return swatches.filter((s) => {
          const c = s && s.colour;
          if (seen.has(c)) return false;
          seen.add(c);
          return true;
        });
      }

      // Build a Shopify CDN URL at _SIZExSIZE — mirrors marqo.js resizeImageUrl (M).
      #resizeImg(url, size) {
        if (!url) return url;
        try {
          const u = new URL(url, location.origin);
          const dot = u.pathname.lastIndexOf('.');
          if (dot === -1) return url;
          const ext = u.pathname.slice(dot);
          const base = u.pathname.slice(0, dot).replace(/_\d+x\d+$/, '');
          u.pathname = `${base}_${size}x${size}${ext}`;
          return u.toString();
        } catch { return url; }
      }

      // Swatch click → swap the card's main image to that variant. Marqo wires the
      // radios and stores each swatch image in data-swt, but ships no change handler.
      #onSwatchChange(input) {
        const card = input.closest('.marqo-product-card');
        const item = input.closest('.dev-plp-card__swt-item');
        if (!card || !item) return;
        const holder = item.querySelector('.image-holder');
        const swt = item.querySelector('.dev-plp-card__swt-img');
        const src = (holder && holder.dataset.swt) || (swt && (swt.currentSrc || swt.src));
        const main = card.querySelector('.card__image img');
        if (!src || !main) return;
        main.removeAttribute('srcset');
        main.src = this.#upsizeImg(src, 340); // swatch url is _150x150 — bump to card size
        main.setAttribute('data-src', main.src);
        if (swt && swt.alt) main.alt = swt.alt;
      }

      // Rewrite just the _WxH dimension suffix to the requested size — mirrors dev-collection.js.
      #upsizeImg(url, size) {
        return String(url).replace(/_\d+x\d+(\.\w+)(?=$|[?#])/, `_${size}x${size}$1`);
      }

      #scrollByPage(dir) {
        const card = this.track.querySelector('.marqo-product-card');
        const gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
        const step = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth;
        const perView = Math.max(1, Math.round(this.track.clientWidth / step));
        this.track.scrollBy({ left: dir * step * perView, behavior: 'smooth' });
      }

      #updateButtons() {
        if (!this.prevBtn || !this.nextBtn) return;
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        const max = scrollWidth - clientWidth;
        this.prevBtn.disabled = scrollLeft <= 1;
        this.nextBtn.disabled = scrollLeft >= max - 1;
      }
    }
  );
})();
